import { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { StaffShell } from '../../ui/StaffShell';
import { ContextError } from '../../ui/ContextError';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { adminNav, AdminSection, usePlatformAdmin } from './adminData';
import { errorText, rpc, SupportStatus } from './api';
import type { StaffContext } from '../../lib/staffContext';

export type AdminPageProps = {
  ctx: StaffContext;
  support: SupportStatus;
  refreshSupport: () => Promise<void>;
};

// Every platform-admin page: the gate, the sidebar nav, and — while this
// sign-in is in support mode — the support banner on every screen (spec G1).
export function AdminFrame({
  section,
  title,
  actions,
  children,
}: {
  section: AdminSection;
  title: string;
  actions?: ReactNode;
  children: (p: AdminPageProps) => ReactNode;
}) {
  const { t } = useI18n();
  const { ctx, ctxError, support, approvals, refreshSupport, signOut } = usePlatformAdmin();

  if (ctxError) return <ContextError message={ctxError} />;
  if (!ctx) return <main className="page-loading" role="status">{t.loading}</main>;

  return (
    <StaffShell
      role={t['role.platformAdmin']}
      nav={adminNav(t, section, approvals)}
      title={title}
      who={ctx.display_name}
      ctx={ctx}
      onSignOut={signOut}
      actions={actions}
      banner={support.active ? <SupportBanner support={support} onLeft={refreshSupport} /> : undefined}
    >
      {children({ ctx, support, refreshSupport })}
    </StaffShell>
  );
}

function minutesLeft(iso: string): number {
  return Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 60000));
}

export function SupportBanner({
  support,
  onLeft,
}: {
  support: Extract<SupportStatus, { active: true }>;
  onLeft: () => Promise<void>;
}) {
  const { t } = useI18n();
  const announce = useAnnounce();
  const [left, setLeft] = useState(() => minutesLeft(support.expires_at));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = window.setInterval(() => setLeft(minutesLeft(support.expires_at)), 30000);
    return () => window.clearInterval(id);
  }, [support.expires_at]);

  async function leave() {
    setBusy(true);
    const r = await rpc('platform_exit_support');
    setBusy(false);
    if (r.error) return setError(errorText(t, r.error));
    announce(t['pa.supportLeft']);
    await onLeft();
  }

  return (
    <div className="support-banner" role="region" aria-label={t['pa.supportRegion']}>
      <p>
        <strong>{t['pa.supportBanner']}</strong>{' '}
        <span>
          <Link to={`/admin/schools/${support.school_id}`}>
            {support.school_name} · {support.school_code}
          </Link>{' '}
          · {t['pa.supportEnds'](left)}
        </span>
      </p>
      <button type="button" className="btn btn-dark" onClick={leave} disabled={busy}>
        {t['pa.supportLeave']}
      </button>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
